import { useRouter } from "next/router";
import { useEffect, useState, useRef } from "react";
import {
  GatekeeperOAuthClient,
  GatekeeperOAuthCallbackHandler,
} from "../../utils/gatekeeper-oauth-client";
import { OAuthLoadingScreen } from "./OAuthLoadingScreen";

interface GatekeeperOAuthCallbackProps {
  /** Gatekeeper OAuth client instance */
  oAuthClient: GatekeeperOAuthClient;
  /** Function to handle the complete OAuth callback flow (exchange token + set token) */
  onOAuthCallback: GatekeeperOAuthCallbackHandler;
  /** Route to redirect to after successful authentication */
  successRedirectRoute: string;
  /** Route to redirect to on error (with optional error query param) */
  errorRedirectRoute: string;
  /** Optional custom loading component */
  LoadingComponent?: React.ComponentType;
  /** Optional custom error component */
  ErrorComponent?: React.ComponentType<{ error: string }>;
}

const DefaultLoadingComponent = () => <OAuthLoadingScreen />;

const DefaultErrorComponent: React.FC<{ error: string }> = ({ error }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      fontFamily: "system-ui, sans-serif",
      backgroundColor: "#f8f9fa",
    }}
  >
    <div
      style={{
        padding: "40px",
        borderRadius: "8px",
        backgroundColor: "white",
        textAlign: "center",
        maxWidth: "400px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          backgroundColor: "#ff4d4f",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          color: "white",
          fontSize: "20px",
        }}
      >
        ⚠️
      </div>

      <h2 style={{ color: "#333", marginBottom: "16px", fontWeight: 600 }}>
        Authentication Failed
      </h2>

      <p
        style={{
          color: "#666",
          fontSize: "14px",
          lineHeight: "1.5",
          marginBottom: "16px",
        }}
      >
        {error || "An error occurred during authentication."}
      </p>

      <p style={{ color: "#999", fontSize: "12px" }}>
        Redirecting you back to the login page...
      </p>
    </div>
  </div>
);

/**
 * Reusable OAuth callback component for Gatekeeper OAuth integration
 *
 * Usage:
 * ```typescript
 * import { GatekeeperOAuthCallback } from "@dreambigger/shared/src/components/gatekeeper";
 *
 * const CallbackPage = () => {
 *   const tokenHelper = useSessionToken();
 *
 *   const handleOAuthCallback = async (params) => {
 *     // Your app's API call to exchange OAuth code for token
 *     const response = await fetch(`/api/oauth/callback`, { ... });
 *     const result = await response.json();
 *
 *     // Validate and set token
 *     if (!result || !result.jwt) {
 *       throw new Error("No token received from OAuth callback");
 *     }
 *     tokenHelper.set(result.jwt);
 *
 *     return result;
 *   };
 *
 *   return (
 *     <GatekeeperOAuthCallback
 *       oAuthClient={oAuthClient}
 *       onOAuthCallback={handleOAuthCallback}
 *       successRedirectRoute="/dashboard"
 *       errorRedirectRoute="/login"
 *     />
 *   );
 * };
 * ```
 */
const GatekeeperOAuthCallback: React.FC<GatekeeperOAuthCallbackProps> = ({
  oAuthClient,
  onOAuthCallback,
  successRedirectRoute,
  errorRedirectRoute,
  LoadingComponent = DefaultLoadingComponent,
  ErrorComponent = DefaultErrorComponent,
}) => {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const processingRef = useRef(false);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if we have the required query parameters
      const { code, state } = router.query;
      if (!code || !state) {
        return;
      }

      // Prevent multiple OAuth callback attempts using refs
      if (processingRef.current || hasProcessedRef.current) {
        return;
      }

      processingRef.current = true;

      try {
        if (!oAuthClient.isCallback()) {
          // Not an OAuth callback, redirect to error route
          router.replace(errorRedirectRoute);
          return;
        }

        // Process OAuth callback - let the caller handle token validation and setting
        await oAuthClient.handleCallback(onOAuthCallback);

        // Mark as successfully processed
        hasProcessedRef.current = true;

        // Success - redirect to success route
        router.replace(successRedirectRoute);
      } catch (error) {
        console.error("OAuth callback failed:", error);

        const errorMessage =
          error instanceof Error ? error.message : "Authentication failed";
        setError(errorMessage);
        setLoading(false);

        // Mark as processed even on error to prevent retries
        hasProcessedRef.current = true;

        // Redirect to login with error after a delay
        setTimeout(() => {
          const errorUrl = errorRedirectRoute.includes("?")
            ? `${errorRedirectRoute}&error=${encodeURIComponent(errorMessage)}`
            : `${errorRedirectRoute}?error=${encodeURIComponent(errorMessage)}`;
          router.replace(errorUrl);
        }, 3000);
      } finally {
        processingRef.current = false;
      }
    };

    if (router.isReady) {
      handleOAuthCallback();
    }
  }, [
    router.isReady,
    router.query.code,
    router.query.state,
    oAuthClient,
    onOAuthCallback,
    successRedirectRoute,
    errorRedirectRoute,
  ]);

  if (loading) {
    return <LoadingComponent />;
  }

  return <ErrorComponent error={error || "Unknown error"} />;
};

export default GatekeeperOAuthCallback;
